import { z } from 'zod';
import { PAGINATION, Portal, MerchantStatus, MerchantUserRole, ACTIVE_PAYMENT_METHODS, OrderStatus, BankAccountKind, AccountType, ListUnit, ParcelSize, ParcelPayer, VehicleType, MODULOS_ADMIN, UserStatus, DiscountType, BillingPlan, ACCIONES_ADMIN } from '@kick/types';

// src/common.ts
var uuidSchema = z.string().uuid("Identificador inv\xE1lido");
var phoneSchema = z.string().trim().transform((v) => v.replace(/[\s\-()]/g, "")).transform((v) => v.replace(/^(\+?57)/, "")).refine((v) => /^3\d{9}$/.test(v), {
  message: "Ingresa un celular colombiano v\xE1lido (10 d\xEDgitos, empieza por 3)"
});
var emailSchema = z.string().trim().toLowerCase().email("Correo electr\xF3nico inv\xE1lido").max(160);
var usernameSchema = z.string().trim().toLowerCase().min(3, "El usuario debe tener al menos 3 caracteres").max(30, "El usuario no puede superar 30 caracteres").regex(
  /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/,
  "Solo letras, n\xFAmeros, punto, guion y guion bajo (sin empezar ni terminar en separador)"
).refine((v) => !/^\d+$/.test(v), {
  message: "El usuario no puede ser solo n\xFAmeros"
});
var passwordSchema = z.string().min(8, "La contrase\xF1a debe tener al menos 8 caracteres").max(72, "La contrase\xF1a es demasiado larga").regex(/[a-z]/, "Debe incluir al menos una min\xFAscula").regex(/[A-Z]/, "Debe incluir al menos una may\xFAscula").regex(/\d/, "Debe incluir al menos un n\xFAmero");
var latLngSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180)
});
var moneySchema = z.number().int("El importe debe ser un n\xFAmero entero de centavos").nonnegative("El importe no puede ser negativo");
var signedMoneySchema = z.number().int();
var slugSchema = z.string().trim().toLowerCase().min(2).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Solo min\xFAsculas, n\xFAmeros y guiones");
var paginationSchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(PAGINATION.maxLimit).default(PAGINATION.defaultLimit)
});
var dateRangeSchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date()
}).refine((v) => v.from <= v.to, {
  message: "La fecha inicial debe ser anterior a la final",
  path: ["from"]
});
var timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Formato de hora inv\xE1lido (HH:MM)");
var loginSchema = z.object({
  /** Nombre de usuario, no correo. */
  username: usernameSchema,
  password: z.string().min(1, "Ingresa tu contrase\xF1a"),
  /** El portal desde el que se intenta entrar; el servidor valida el rol contra él. */
  portal: z.nativeEnum(Portal)
});
var registerCustomerSchema = z.object({
  username: usernameSchema,
  fullName: z.string().trim().min(3, "Ingresa tu nombre completo").max(150),
  /** Opcional: sin correo no hay recuperación de contraseña por email. */
  email: emailSchema.optional(),
  phone: phoneSchema,
  password: passwordSchema,
  confirmPassword: z.string(),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: "Debes aceptar los t\xE9rminos y condiciones" })
  })
}).refine((v) => v.password === v.confirmPassword, {
  message: "Las contrase\xF1as no coinciden",
  path: ["confirmPassword"]
});
var checkUsernameSchema = z.object({
  username: usernameSchema
});
var refreshSchema = z.object({
  /** Solo lo envía la app móvil; en web viaja por cookie httpOnly. */
  refreshToken: z.string().optional()
});
var forgotPasswordSchema = z.object({
  username: usernameSchema
});
var resetPasswordSchema = z.object({
  token: z.string().min(20, "Enlace inv\xE1lido"),
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((v) => v.password === v.confirmPassword, {
  message: "Las contrase\xF1as no coinciden",
  path: ["confirmPassword"]
});
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Ingresa tu contrase\xF1a actual"),
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((v) => v.password === v.confirmPassword, {
  message: "Las contrase\xF1as no coinciden",
  path: ["confirmPassword"]
}).refine((v) => v.currentPassword !== v.password, {
  message: "La nueva contrase\xF1a debe ser distinta de la actual",
  path: ["password"]
});
var updateProfileSchema = z.object({
  fullName: z.string().trim().min(3).max(150).optional(),
  email: emailSchema.nullable().optional(),
  phone: phoneSchema.optional(),
  avatarUrl: z.string().url().nullable().optional()
  // El nombre de usuario no se edita aquí: cambiarlo rompe referencias y
  // habilita suplantación. Si hace falta, lo cambia un administrador.
});
var createMerchantSchema = z.object({
  name: z.string().trim().min(3, "Nombre demasiado corto").max(150),
  slug: slugSchema.optional(),
  categoryId: uuidSchema,
  description: z.string().trim().max(1e3).optional(),
  phone: phoneSchema,
  /**
   * Opcional: la columna admite nulo y una panadería de barrio puede no tener
   * correo. Exigirlo impedía dar de alta comercios reales.
   */
  email: emailSchema.optional(),
  addressLine: z.string().trim().min(5, "Direcci\xF3n demasiado corta").max(250),
  city: z.string().trim().min(2).max(100).default("Medell\xEDn"),
  location: latLngSchema,
  commissionRate: z.number().min(0).max(100).optional(),
  avgPrepMinutes: z.number().int().min(1).max(180).default(20),
  minOrderAmount: moneySchema.default(0),
  /** Pedido por lista: el cliente escribe su lista y el comercio la cotiza. */
  listOrdering: z.boolean().optional(),
  zoneId: uuidSchema.nullable().optional(),
  /** Datos del usuario dueño; el admin lo crea junto con el comercio. */
  owner: z.object({
    username: usernameSchema,
    fullName: z.string().trim().min(3).max(150),
    email: emailSchema.optional(),
    phone: phoneSchema
  }).optional()
});
var updateMerchantSchema = createMerchantSchema.omit({ owner: true, slug: true }).partial().extend({
  logoUrl: z.string().url().nullable().optional(),
  coverUrl: z.string().url().nullable().optional(),
  isOpenNow: z.boolean().optional(),
  status: z.nativeEnum(MerchantStatus).optional()
});
var merchantScheduleSchema = z.object({
  weekday: z.number().int().min(0).max(6),
  opensAt: timeSchema,
  closesAt: timeSchema,
  isClosed: z.boolean().default(false)
}).refine((v) => v.isClosed || v.opensAt < v.closesAt, {
  message: "La hora de apertura debe ser anterior a la de cierre",
  path: ["closesAt"]
});
var merchantSchedulesSchema = z.array(merchantScheduleSchema).max(14);
var createMerchantUserSchema = z.object({
  username: usernameSchema,
  fullName: z.string().trim().min(3).max(150),
  email: emailSchema.optional(),
  phone: phoneSchema,
  role: z.nativeEnum(MerchantUserRole).default(MerchantUserRole.STAFF)
});
var productCategorySchema = z.object({
  name: z.string().trim().min(2, "Nombre demasiado corto").max(100),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
});
var reorderSchema = z.object({
  ids: z.array(uuidSchema).min(1, "Nada que reordenar")
});
var productOptionSchema = z.object({
  id: uuidSchema.optional(),
  name: z.string().trim().min(1, "Ingresa el nombre de la opci\xF3n").max(100),
  priceDelta: signedMoneySchema.default(0),
  isAvailable: z.boolean().default(true),
  sortOrder: z.number().int().min(0).default(0)
});
var productOptionGroupSchema = z.object({
  id: uuidSchema.optional(),
  name: z.string().trim().min(1, "Ingresa el nombre del grupo").max(100),
  minSelect: z.number().int().min(0).max(20).default(0),
  maxSelect: z.number().int().min(1).max(20).default(1),
  isRequired: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
  options: z.array(productOptionSchema).min(1, "Agrega al menos una opci\xF3n").max(30)
}).refine((v) => v.minSelect <= v.maxSelect, {
  message: "El m\xEDnimo no puede ser mayor que el m\xE1ximo",
  path: ["minSelect"]
}).refine((v) => v.maxSelect <= v.options.length, {
  message: "El m\xE1ximo no puede superar la cantidad de opciones",
  path: ["maxSelect"]
}).refine((v) => !v.isRequired || v.minSelect >= 1, {
  message: "Un grupo obligatorio debe exigir al menos una selecci\xF3n",
  path: ["minSelect"]
});
var productBaseSchema = z.object({
  categoryId: uuidSchema,
  name: z.string().trim().min(2, "Nombre demasiado corto").max(150),
  description: z.string().trim().max(1e3).optional(),
  price: moneySchema.min(1, "El precio debe ser mayor que cero"),
  compareAtPrice: moneySchema.nullable().optional(),
  imageUrl: z.string().url().nullable().optional(),
  isAvailable: z.boolean().default(true),
  trackStock: z.boolean().default(false),
  stockQty: z.number().int().min(0).nullable().optional(),
  prepMinutes: z.number().int().min(1).max(180).nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  tags: z.array(z.string().trim().max(30)).max(10).default([]),
  optionGroups: z.array(productOptionGroupSchema).max(10).default([])
});
var createProductSchema = productBaseSchema.refine((v) => !v.compareAtPrice || v.compareAtPrice > v.price, {
  message: "El precio tachado debe ser mayor que el precio actual",
  path: ["compareAtPrice"]
}).refine((v) => !v.trackStock || v.stockQty != null, {
  message: "Indica la cantidad en inventario",
  path: ["stockQty"]
});
var updateProductSchema = productBaseSchema.partial();
var toggleAvailabilitySchema = z.object({
  isAvailable: z.boolean()
});
var merchantSearchSchema = z.object({
  lat: z.coerce.number().min(-90).max(90).optional(),
  lng: z.coerce.number().min(-180).max(180).optional(),
  radius: z.coerce.number().int().min(500).max(3e4).default(8e3),
  categoryId: uuidSchema.optional(),
  // z.coerce.boolean() convierte "false" en true; se compara el texto explícitamente.
  open: z.enum(["true", "false"]).optional().transform((v) => v === void 0 ? void 0 : v === "true"),
  minRating: z.coerce.number().min(0).max(5).optional(),
  sort: z.enum(["distance", "rating", "delivery_fee", "popular"]).default("distance"),
  q: z.string().trim().max(100).optional()
});
var globalSearchSchema = z.object({
  q: z.string().trim().min(2, "Escribe al menos 2 caracteres").max(100),
  lat: z.coerce.number().optional(),
  lng: z.coerce.number().optional()
});
var customerAddressSchema = z.object({
  label: z.string().trim().min(1, "Ponle un nombre a la direcci\xF3n").max(50),
  addressLine: z.string().trim().min(5, "Direcci\xF3n demasiado corta").max(250),
  details: z.string().trim().max(150).optional(),
  neighborhood: z.string().trim().max(100).optional(),
  city: z.string().trim().min(2).max(100).default("Medell\xEDn"),
  location: latLngSchema,
  isDefault: z.boolean().default(false),
  contactPhone: phoneSchema.optional()
});
var cartItemSchema = z.object({
  productId: uuidSchema,
  quantity: z.number().int().min(1, "Cantidad m\xEDnima 1").max(50, "Cantidad m\xE1xima 50"),
  /** Solo los IDs: el servidor recalcula nombres y precios desde la base de datos. */
  optionIds: z.array(uuidSchema).max(30).default([]),
  notes: z.string().trim().max(200).optional()
});
var createOrderSchema = z.object({
  merchantId: uuidSchema,
  addressId: uuidSchema,
  items: z.array(cartItemSchema).min(1, "El carrito est\xE1 vac\xEDo").max(50),
  /**
   * Solo los métodos que la plataforma acepta hoy.
   *
   * Tarjeta, PSE y billetera existen en el enum para cuando se conecte una
   * pasarela, pero aceptarlos ahora crearía pedidos que nadie puede cobrar.
   */
  paymentMethod: z.enum(
    ACTIVE_PAYMENT_METHODS
  ),
  couponCode: z.string().trim().toUpperCase().max(40).optional(),
  tip: moneySchema.default(0),
  customerNotes: z.string().trim().max(300).optional(),
  /**
   * Total que el cliente vio en pantalla. El servidor recalcula todo y rechaza
   * el pedido si no coincide, para que el usuario nunca pague algo distinto
   * a lo que confirmó. Nunca se usa como fuente del importe a cobrar.
   */
  expectedTotal: moneySchema
});
var quoteOrderSchema = createOrderSchema.omit({
  expectedTotal: true,
  paymentMethod: true
});
var updateOrderStatusSchema = z.object({
  status: z.nativeEnum(OrderStatus),
  reason: z.string().trim().max(300).optional()
});
var rejectOrderSchema = z.object({
  reason: z.string().trim().min(5, "Indica el motivo del rechazo").max(300)
});
var cancelOrderSchema = z.object({
  reason: z.string().trim().min(5, "Indica el motivo de la cancelaci\xF3n").max(300)
});
var acceptOrderSchema = z.object({
  /** Permite al comercio ajustar el tiempo de preparación al aceptar. */
  prepMinutes: z.number().int().min(1).max(180).optional()
});
var respondAssignmentSchema = z.object({
  accept: z.boolean(),
  rejectReason: z.string().trim().max(200).optional()
});
var releaseOrderSchema = z.object({
  reason: z.string().trim().min(5, "Cuenta por qu\xE9 lo liberas (m\xEDnimo 5 letras)").max(300, "M\xE1ximo 300 caracteres")
});
var locationUpdateSchema = z.object({
  location: latLngSchema,
  heading: z.number().min(0).max(360).optional(),
  speedKmh: z.number().min(0).max(200).optional(),
  accuracyM: z.number().min(0).optional(),
  recordedAt: z.string().datetime(),
  orderId: uuidSchema.optional()
});
var locationBatchSchema = z.object({
  points: z.array(locationUpdateSchema).min(1).max(200)
});
var courierAccountSchema = z.object({
  type: z.nativeEnum(AccountType),
  bankName: z.string().trim().max(80).optional(),
  accountKind: z.nativeEnum(BankAccountKind).nullable().optional(),
  accountNumber: z.string().trim().min(6, "N\xFAmero demasiado corto").max(40).regex(/^[0-9-]+$/, "Solo n\xFAmeros"),
  holderName: z.string().trim().min(3, "Nombre demasiado corto").max(150),
  holderDocument: z.string().trim().max(30).optional(),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true)
}).refine(
  (v) => v.type === AccountType.NEQUI || v.type === AccountType.DAVIPLATA || !!v.accountKind,
  { message: "Indica si es de ahorros o corriente", path: ["accountKind"] }
).refine((v) => v.type !== AccountType.OTRO_BANCO || !!v.bankName, {
  message: "Indica el nombre del banco",
  path: ["bankName"]
});
var uploadReceiptSchema = z.object({
  accountId: uuidSchema.optional(),
  reference: z.string().trim().max(60).optional(),
  declaredAmount: z.coerce.number().int().min(0).optional()
});
var reviewReceiptSchema = z.object({
  approve: z.boolean(),
  /** Obligatorio al rechazar: el cliente merece saber qué pasó. */
  reason: z.string().trim().max(300).optional(),
  /** Qué hacer con el pedido tras rechazar el comprobante. */
  then: z.enum(["CASH", "CANCEL"]).optional()
}).refine((v) => v.approve || (v.reason?.length ?? 0) >= 5, {
  message: "Explica por qu\xE9 lo rechazas",
  path: ["reason"]
}).refine((v) => v.approve || !!v.then, {
  message: "Indica si se cobra en efectivo o se cancela el pedido",
  path: ["then"]
});
var confirmDeliverySchema = z.object({
  /** Efectivo recibido, en centavos; obligatorio si el pago es CASH. */
  cashCollected: moneySchema.optional(),
  evidenceUrl: z.string().url().optional(),
  notes: z.string().trim().max(300).optional()
});
var rateOrderSchema = z.object({
  merchantScore: z.number().int().min(1).max(5),
  courierScore: z.number().int().min(1).max(5).optional(),
  comment: z.string().trim().max(500).optional()
});
var orderFilterSchema = z.object({
  status: z.union([z.nativeEnum(OrderStatus), z.array(z.nativeEnum(OrderStatus))]).optional().transform((v) => v === void 0 ? void 0 : Array.isArray(v) ? v : [v]),
  merchantId: uuidSchema.optional(),
  courierId: uuidSchema.optional(),
  customerId: uuidSchema.optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  code: z.string().trim().max(12).optional()
});
var listItemSchema = z.object({
  name: z.string().trim().min(2, "Escribe el producto").max(200),
  quantity: z.number().int().min(1).max(999).default(1),
  unit: z.nativeEnum(ListUnit).default(ListUnit.UNIT),
  note: z.string().trim().max(200).optional(),
  /** «Si no hay, sugiéreme otro» o «si no hay, quítalo». */
  allowSubstitute: z.boolean().default(true)
});
var createListOrderSchema = z.object({
  merchantId: uuidSchema,
  addressId: uuidSchema,
  items: z.array(listItemSchema).min(1, "Agrega al menos un producto").max(80),
  paymentMethod: z.enum(
    ACTIVE_PAYMENT_METHODS
  ),
  /** Tope de presupuesto opcional: el comercio lo ve mientras cotiza. */
  budgetCap: moneySchema.optional(),
  customerNotes: z.string().trim().max(300).optional()
});
var reviewListItemSchema = z.object({
  status: z.enum(["CONFIRMED", "UNAVAILABLE"]),
  /** Precio del renglón completo, cantidad incluida. */
  price: moneySchema.optional(),
  suggestionName: z.string().trim().min(2).max(200).optional(),
  suggestionPrice: moneySchema.optional()
}).refine((v) => v.status !== "CONFIRMED" || (v.price ?? 0) > 0, {
  message: "Ponle el precio al rengl\xF3n",
  path: ["price"]
}).refine(
  (v) => v.status !== "UNAVAILABLE" || !v.suggestionName || (v.suggestionPrice ?? 0) > 0,
  { message: "Ponle el precio al reemplazo", path: ["suggestionPrice"] }
);
var listSuggestionDecisionSchema = z.object({ accept: z.boolean() });
var savedListSchema = z.object({
  merchantId: uuidSchema,
  name: z.string().trim().min(2, "Ponle nombre a la lista").max(60),
  items: z.array(listItemSchema).min(1).max(80)
});
var parcelPointSchema = z.object({
  addressLine: z.string().trim().min(5, "Escribe la direcci\xF3n completa").max(250),
  details: z.string().trim().max(150).optional(),
  location: latLngSchema
});
var quoteParcelSchema = z.object({
  origin: latLngSchema,
  destination: latLngSchema,
  size: z.nativeEnum(ParcelSize)
});
var createParcelSchema = z.object({
  pickup: parcelPointSchema,
  dropoff: parcelPointSchema,
  size: z.nativeEnum(ParcelSize),
  description: z.string().trim().min(3, "Dinos qu\xE9 est\xE1s mandando").max(300),
  /** Opcional y sin promesa de indemnización: informa al domiciliario. */
  declaredValue: moneySchema.optional(),
  payer: z.nativeEnum(ParcelPayer),
  paymentMethod: z.enum(
    ACTIVE_PAYMENT_METHODS
  ),
  recipientName: z.string().trim().min(3, "Nombre de quien recibe").max(140),
  recipientPhone: phoneSchema,
  /** El teléfono de quien entrega el paquete al domiciliario. */
  senderPhone: phoneSchema,
  customerNotes: z.string().trim().max(300).optional(),
  /**
   * Para cuándo, si no es para ya.
   *
   * Ausente significa «lo antes posible», que es el caso normal. Con fecha, la
   * encomienda queda a la espera de que administración la apruebe: la ventana
   * mínima y máxima las comprueba el servidor contra los ajustes, porque son
   * reglas de negocio que se editan sin desplegar.
   */
  scheduledFor: z.coerce.date().optional()
});
var createMerchantParcelSchema = z.object({
  dropoff: parcelPointSchema,
  size: z.nativeEnum(ParcelSize),
  description: z.string().trim().min(3, "Dinos qu\xE9 est\xE1s mandando").max(300),
  declaredValue: moneySchema.optional(),
  recipientName: z.string().trim().min(3, "Nombre de quien recibe").max(140),
  recipientPhone: phoneSchema,
  /** Dónde exactamente dentro del local: «en la bodega», «pregunta por Luis». */
  pickupDetails: z.string().trim().max(150).optional(),
  customerNotes: z.string().trim().max(300).optional(),
  scheduledFor: z.coerce.date().optional()
});
var batchItemSchema = z.object({
  dropoff: parcelPointSchema,
  recipientName: z.string().trim().min(3, "Nombre de quien recibe").max(140),
  recipientPhone: phoneSchema,
  description: z.string().trim().min(2, "Dinos qu\xE9 mandas").max(300),
  size: z.nativeEnum(ParcelSize).optional()
});
var createMerchantBatchSchema = z.object({
  items: z.array(batchItemSchema).min(1, "Agrega al menos un env\xEDo").max(60),
  /** Tamaño por defecto para los que no lo digan. */
  size: z.nativeEnum(ParcelSize).default(ParcelSize.MEDIUM),
  pickupDetails: z.string().trim().max(150).optional(),
  notes: z.string().trim().max(300).optional()
});
var quoteBatchSchema = z.object({
  items: z.array(z.object({ location: latLngSchema, size: z.nativeEnum(ParcelSize).optional() })).min(1).max(60),
  size: z.nativeEnum(ParcelSize).default(ParcelSize.MEDIUM)
});
var createNonPartnerParcelSchema = z.object({
  store: z.object({
    name: z.string().trim().min(2, "Nombre del comercio").max(140),
    addressLine: z.string().trim().min(5, "Escribe la direcci\xF3n completa").max(250),
    details: z.string().trim().max(150).optional(),
    location: latLngSchema
  }),
  purchaseDescription: z.string().trim().min(3, "Dinos qu\xE9 hay que comprar").max(300),
  purchaseAmount: moneySchema.optional(),
  dropoff: parcelPointSchema,
  recipientName: z.string().trim().min(3, "Nombre de quien recibe").max(140),
  recipientPhone: phoneSchema,
  customerNotes: z.string().trim().max(300).optional()
});
var manualAssignSchema = z.object({
  orderIds: z.array(uuidSchema).min(1).max(100),
  courierId: uuidSchema
});
var manualReleaseSchema = z.object({
  orderIds: z.array(uuidSchema).min(1).max(100)
});
var parcelDecisionSchema = z.object({
  approve: z.boolean(),
  /** Obligatorio al rechazar: el cliente merece saber por qué. */
  note: z.string().trim().max(300).optional()
});
var deliveryZoneSchema = z.object({
  name: z.string().trim().min(2, "Nombre demasiado corto").max(100),
  city: z.string().trim().min(2).max(100).default("Medell\xEDn"),
  /** Anillo exterior del polígono; el servidor lo cierra si hace falta. */
  area: z.array(latLngSchema).min(3, "Un pol\xEDgono necesita al menos 3 puntos").max(500),
  baseFee: moneySchema,
  perKmFee: moneySchema,
  freeDeliveryOver: moneySchema.nullable().optional(),
  isActive: z.boolean().default(true)
});
var platformSettingsSchema = z.object({
  commissionRate: z.number().min(0).max(100).optional(),
  serviceFee: moneySchema.optional(),
  /** Tarifa de lluvia encendida: se suma a los pedidos nuevos. El valor es `serviceFee`. */
  rainFeeActive: z.boolean().optional(),
  assignmentRadiusMeters: z.number().int().min(500).max(3e4).optional(),
  assignmentOfferTimeoutSeconds: z.number().int().min(10).max(180).optional(),
  /** Rondas de oferta antes de rendirse y mandar el pedido a la lista de pendientes. */
  assignmentMaxAttempts: z.number().int().min(1).max(20).optional(),
  minOrderAmount: moneySchema.optional(),
  tipPresets: z.array(moneySchema).max(6).optional(),
  /** Lo que gana el domiciliario: base fija más adicional por kilómetro. */
  courierBaseFee: moneySchema.optional(),
  courierPerKmFee: moneySchema.optional(),
  parcelDocumentFee: moneySchema.optional(),
  parcelMediumFee: moneySchema.optional(),
  parcelLargeFee: moneySchema.optional(),
  maxActiveOrdersPerCourier: z.number().int().min(1).max(10).optional(),
  platformOpen: z.boolean().optional(),
  platformClosedMessage: z.string().trim().min(5).max(300).optional(),
  courierBalanceCutPercent: z.number().int().min(0).max(100).optional(),
  nonPartnerFee: moneySchema.optional(),
  listReviewAlertMinutes: z.number().int().min(1).max(120).optional(),
  rechargeAccountInfo: z.string().trim().max(500).optional(),
  premiumOrdersPerMonth: z.number().int().min(1).max(1e3).optional(),
  premiumDiscountPercent: z.number().int().min(0).max(50).optional(),
  courierBonusDeliveries: z.number().int().min(1).max(1e4).optional(),
  courierBonusAmount: moneySchema.optional(),
  parcelScheduleMinHours: z.number().int().min(0).max(72).optional(),
  parcelScheduleMaxDays: z.number().int().min(1).max(180).optional(),
  parcelRepriceThresholdPercent: z.number().int().min(0).max(100).optional(),
  parcelRepriceWindowMinutes: z.number().int().min(5).max(240).optional()
});
var merchantCategorySchema = z.object({
  name: z.string().trim().min(2).max(100),
  icon: z.string().trim().max(50).optional(),
  imageUrl: z.string().url().nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
});
var createCourierSchema = z.object({
  username: usernameSchema,
  fullName: z.string().trim().min(3).max(150),
  email: emailSchema.optional(),
  phone: phoneSchema,
  documentId: z.string().trim().min(5, "Documento inv\xE1lido").max(30),
  vehicleType: z.nativeEnum(VehicleType),
  plate: z.string().trim().toUpperCase().max(10).nullable().optional().transform((v) => v || null)
});
var verifyCourierSchema = z.object({
  isVerified: z.boolean(),
  reason: z.string().trim().max(300).optional()
});
var MODULOS = MODULOS_ADMIN.map((m) => m.key);
var ACCIONES = Object.keys(ACCIONES_ADMIN);
var adminPermissionsSchema = z.record(z.enum(MODULOS), z.array(z.enum(ACCIONES))).transform((mapa) => {
  const limpio = {};
  for (const modulo of MODULOS_ADMIN) {
    const pedidas = mapa[modulo.key];
    if (!pedidas || pedidas.length === 0) continue;
    const validas = pedidas.filter((a) => modulo.acciones.includes(a));
    if (validas.length === 0) continue;
    limpio[modulo.key] = validas.includes("ver") ? validas : ["ver", ...validas];
  }
  return limpio;
});
var createAdminSchema = z.object({
  username: usernameSchema,
  fullName: z.string().trim().min(3).max(150),
  email: emailSchema.optional(),
  phone: phoneSchema.optional(),
  /** `null` = acceso total, como el dueño. */
  permissions: adminPermissionsSchema.nullable()
});
var updateAdminPermissionsSchema = z.object({
  permissions: adminPermissionsSchema.nullable()
});
var createRechargeSchema = z.object({
  // Llega como texto: viaja en multipart junto al comprobante.
  amount: z.coerce.number().int().min(1, "Escribe el valor que transferiste")
});
var reviewRechargeSchema = z.object({
  approve: z.boolean(),
  /** Si el comprobante dice otra cifra, se aprueba por esa cifra. */
  amount: moneySchema.optional(),
  note: z.string().trim().max(300).optional()
}).refine((v) => v.approve || (v.note?.length ?? 0) >= 3, {
  message: "Dile al domiciliario por qu\xE9 se rechaza",
  path: ["note"]
});
var adjustBalanceSchema = z.object({
  amount: z.number().int().refine((v) => v !== 0, "El ajuste no puede ser cero"),
  note: z.string().trim().min(3, "Indica el motivo del ajuste").max(300)
});
var courierBroadcastSchema = z.object({
  title: z.string().trim().min(3).max(60).default("Kick Domicilios"),
  body: z.string().trim().min(5, "Escribe el mensaje").max(240)
});
var updateUserStatusSchema = z.object({
  status: z.nativeEnum(UserStatus),
  reason: z.string().trim().max(300).optional()
});
var couponSchema = z.object({
  code: z.string().trim().toUpperCase().min(3, "C\xF3digo demasiado corto").max(40).regex(/^[A-Z0-9_-]+$/, "Solo letras, n\xFAmeros, guion y guion bajo"),
  description: z.string().trim().max(200).optional(),
  discountType: z.nativeEnum(DiscountType),
  /** Porcentaje (0-100) o importe en centavos, según discountType. */
  value: z.number().min(0),
  minOrderAmount: moneySchema.default(0),
  maxDiscount: moneySchema.nullable().optional(),
  merchantId: uuidSchema.nullable().optional(),
  validFrom: z.coerce.date(),
  validUntil: z.coerce.date(),
  usageLimit: z.number().int().min(1).nullable().optional(),
  usageLimitPerUser: z.number().int().min(1).nullable().optional(),
  isActive: z.boolean().default(true)
}).refine((v) => v.validFrom < v.validUntil, {
  message: "La vigencia inicial debe ser anterior a la final",
  path: ["validUntil"]
}).refine((v) => v.discountType !== DiscountType.PERCENTAGE || v.value <= 100, {
  message: "Un descuento porcentual no puede superar el 100 %",
  path: ["value"]
});
var reassignCourierSchema = z.object({
  courierId: uuidSchema,
  reason: z.string().trim().min(5, "Indica el motivo de la reasignaci\xF3n").max(300)
});
var auditFilterSchema = z.object({
  actorId: uuidSchema.optional(),
  action: z.string().trim().max(100).optional(),
  entityType: z.string().trim().max(50).optional(),
  entityId: uuidSchema.optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional()
});
var billingPlanSchema = z.object({
  plan: z.nativeEnum(BillingPlan),
  /** Obligatoria en el plan mensual; el servidor lo vuelve a comprobar. */
  monthlyFee: moneySchema.optional()
}).refine((d) => d.plan !== BillingPlan.MONTHLY || !!d.monthlyFee, {
  message: "Ponle el valor de la mensualidad",
  path: ["monthlyFee"]
});
var merchantPaymentSchema = z.object({
  amount: moneySchema.refine((v) => v > 0, "El pago tiene que ser mayor que cero"),
  method: z.string().trim().min(2, "C\xF3mo pag\xF3").max(60),
  reference: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(300).optional()
});
var apiEnvSchema = z.object({
  NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
  PORT: z.coerce.number().int().min(1).max(65535).default(3e3),
  TZ: z.string().default("America/Bogota"),
  API_URL: z.string().url(),
  WEB_URL: z.string().url(),
  PANEL_URL: z.string().url(),
  CORS_ORIGINS: z.string().transform((v) => v.split(",").map((s) => s.trim()).filter(Boolean)),
  COOKIE_DOMAIN: z.string().min(1),
  DATABASE_URL: z.string().url(),
  DATABASE_POOL_MAX: z.coerce.number().int().min(1).max(100).default(10),
  REDIS_HOST: z.string().default("localhost"),
  REDIS_PORT: z.coerce.number().int().default(6380),
  /** Índice de base de Redis: las pruebas usan el 1 para no compartir colas con desarrollo. */
  REDIS_DB: z.coerce.number().int().min(0).max(15).default(0),
  REDIS_PASSWORD: z.string().optional(),
  MINIO_ENDPOINT: z.string().default("localhost"),
  MINIO_PORT: z.coerce.number().int().default(9010),
  MINIO_USE_SSL: z.enum(["true", "false"]).default("false").transform((v) => v === "true"),
  MINIO_ACCESS_KEY: z.string().min(1),
  MINIO_SECRET_KEY: z.string().min(1),
  MINIO_BUCKET: z.string().min(1).default("kick-media"),
  MINIO_PUBLIC_URL: z.string().url(),
  JWT_SECRET: z.string().min(32, "JWT_SECRET debe tener al menos 32 caracteres"),
  /** Un día entero: el comercio entra por la mañana y no vuelve a ver el login. */
  JWT_EXPIRES_IN: z.string().default("24h"),
  REFRESH_EXPIRES_IN: z.string().default("30d"),
  PAYMENT_PROVIDER: z.enum(["cash", "mock", "wompi"]).default("mock"),
  ROUTING_PROVIDER: z.enum(["haversine", "osrm"]).default("haversine"),
  OSRM_URL: z.string().url().optional(),
  SMTP_HOST: z.string().default("localhost"),
  SMTP_PORT: z.coerce.number().int().default(1025),
  SMTP_FROM: z.string().default("Kick Domicilios <no-reply@kick.test>"),
  // Sin credenciales se asume MailHog (sin TLS ni autenticación); con ellas,
  // un SMTP real: el puerto 465 negocia SSL directo y los demás usan STARTTLS.
  SMTP_USER: z.string().optional(),
  SMTP_PASS: z.string().optional(),
  ASSIGNMENT_RADIUS_METERS: z.coerce.number().int().default(5e3),
  ASSIGNMENT_OFFER_TIMEOUT_SECONDS: z.coerce.number().int().default(30),
  DEFAULT_COMMISSION_RATE: z.coerce.number().min(0).max(100).default(15)
});

export { acceptOrderSchema, adjustBalanceSchema, adminPermissionsSchema, apiEnvSchema, auditFilterSchema, batchItemSchema, billingPlanSchema, cancelOrderSchema, cartItemSchema, changePasswordSchema, checkUsernameSchema, confirmDeliverySchema, couponSchema, courierAccountSchema, courierBroadcastSchema, createAdminSchema, createCourierSchema, createListOrderSchema, createMerchantBatchSchema, createMerchantParcelSchema, createMerchantSchema, createMerchantUserSchema, createNonPartnerParcelSchema, createOrderSchema, createParcelSchema, createProductSchema, createRechargeSchema, customerAddressSchema, dateRangeSchema, deliveryZoneSchema, emailSchema, forgotPasswordSchema, globalSearchSchema, latLngSchema, listItemSchema, listSuggestionDecisionSchema, locationBatchSchema, locationUpdateSchema, loginSchema, manualAssignSchema, manualReleaseSchema, merchantCategorySchema, merchantPaymentSchema, merchantScheduleSchema, merchantSchedulesSchema, merchantSearchSchema, moneySchema, orderFilterSchema, paginationSchema, parcelDecisionSchema, parcelPointSchema, passwordSchema, phoneSchema, platformSettingsSchema, productCategorySchema, productOptionGroupSchema, productOptionSchema, quoteBatchSchema, quoteOrderSchema, quoteParcelSchema, rateOrderSchema, reassignCourierSchema, refreshSchema, registerCustomerSchema, rejectOrderSchema, releaseOrderSchema, reorderSchema, resetPasswordSchema, respondAssignmentSchema, reviewListItemSchema, reviewReceiptSchema, reviewRechargeSchema, savedListSchema, signedMoneySchema, slugSchema, timeSchema, toggleAvailabilitySchema, updateAdminPermissionsSchema, updateMerchantSchema, updateOrderStatusSchema, updateProductSchema, updateProfileSchema, updateUserStatusSchema, uploadReceiptSchema, usernameSchema, uuidSchema, verifyCourierSchema };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map